import { useCallback, useMemo } from 'react';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getConfigValue } from 'utils/environment';
import { SERVICE_MODES } from 'utils/service-mode';

import { IAvailabilityProps, StoreServiceModeStatus } from '../types';
import { isMobileOrderingAvailable as checkIsMobileOrderingAvailable, isAvailable } from '../utils';

export type AvailableServiceMode = Omit<
  typeof ServiceMode,
  'CATERING_DELIVERY' | 'CATERING_PICKUP' | 'TABLE_SERVICE' | 'CURBSIDE'
>;

export type ServiceModeCapabilityMap = Partial<Record<ServiceMode, keyof IRestaurant>>;
export type ServiceModesFlagMap = Partial<Record<ServiceMode, boolean>>;

const serviceModeCapabilityMap: ServiceModeCapabilityMap = {
  [ServiceMode.DELIVERY]: 'hasDelivery',
  [ServiceMode.DRIVE_THRU]: 'hasDriveThru',
  [ServiceMode.EAT_IN]: 'hasDineIn',
  [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: 'hasMobileOrderDriveThru',
  [ServiceMode.TAKEOUT]: 'hasTakeOut',
};

export const buildServiceModeStatusesForStore = (
  store: IRestaurant | null,
  storeLocalTime: string | null,
  enabledServiceModesFlagMap: ServiceModesFlagMap
): StoreServiceModeStatus[] => {
  return SERVICE_MODES.map((serviceMode: ServiceMode) => {
    if (store === null || storeLocalTime === null) {
      return {
        serviceMode,
        capable: false,
        available: false,
        featureFlagDisabled: !enabledServiceModesFlagMap[serviceMode],
        open: false,
      } as StoreServiceModeStatus;
    }
    const storeCapabilityKey = serviceModeCapabilityMap[serviceMode];
    const isCapable = !!storeCapabilityKey && !!store[storeCapabilityKey];
    const openAt = store.serviceModeHours?.find(smh => smh.serviceMode === serviceMode)?.open;
    const closeAt = store.serviceModeHours?.find(smh => smh.serviceMode === serviceMode)?.close;
    const isOpen = !!openAt && !!closeAt && storeLocalTime >= openAt && storeLocalTime < closeAt;

    return {
      serviceMode,
      capable: isCapable,
      available: isCapable && isOpen,
      featureFlagDisabled: !enabledServiceModesFlagMap[serviceMode],
      open: isOpen,
    } as StoreServiceModeStatus;
  });
};

export type StoreAvailabilityFn = (store: IRestaurant, storeLocalTime?: string | null) => boolean;

export type StoreAvailabilityByServiceModeFn = (
  store: IRestaurant,
  serviceMode: ServiceMode | null,
  storeLocalTime?: string | null
) => boolean;

export const useStoreAvailabilityFns: () => {
  getStoreServiceModeStatuses: (
    store: IRestaurant,
    storeLocalTime?: string | null
  ) => StoreServiceModeStatus[];
  isOpenForDelivery: StoreAvailabilityFn;
  isOpenForPickup: StoreAvailabilityFn;
  isOpenByServiceMode: StoreAvailabilityByServiceModeFn;
  isServiceModeCapable: StoreAvailabilityByServiceModeFn;
  isServiceModeAvailable: StoreAvailabilityByServiceModeFn;
  isServiceModeDisabled: StoreAvailabilityByServiceModeFn;
  /**
   * Determines a store's availability.
   * A store will be deemed available when all of the below conditions are met:
   * 1. No service mode is selected
   *      OR current service mode is delivery
   *      OR current pickup service mode is available.
   * 2. AND Store POS is available.
   * 3. AND Store has mobile ordering enabled.
   * @param store - The store whose availability needs to be determined.
   * @returns A `boolean` indicating whether the store is available.
   */
  isStoreAvailable: (
    store: IRestaurant,
    serviceMode: ServiceMode | null | undefined,
    storeLocalTime?: string | null
  ) => boolean;
  isPOSAvailable: (store: IRestaurant) => boolean;
  isStoreAvailableForPickup: StoreAvailabilityFn;
  isStoreAvailableForDelivery: StoreAvailabilityFn;
  isMobileOrderingAvailable: (store: IRestaurant) => boolean;
  getPickupAvailableServiceModes: (
    store: IRestaurant,
    storeLocalTime?: string | null
  ) => ServiceMode[];
  getPickupOpenServiceModes: (store: IRestaurant, storeLocalTime?: string | null) => ServiceMode[];
} = () => {
  // TODO: this logic should be moved eventually to the backend use withLDContext
  // to send the launchdarkly context to the backend then we can filter and sort uni the backend
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const enableDriveThru = !useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const enableDineIn = !useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const enableMobileOrderDriveThru = useFlag(LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU);
  const enableTakeout = !useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);
  const mobileOrderDriveThruStores = useFlag(LaunchDarklyFlag.MOBILE_ORDER_DRIVE_THRU_STORES) ?? '';
  const enabledServiceModesFlagMap = useMemo(
    () => ({
      [ServiceMode.DELIVERY]: enableDelivery,
      [ServiceMode.DRIVE_THRU]: enableDriveThru,
      [ServiceMode.EAT_IN]: enableDineIn,
      [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: enableMobileOrderDriveThru,
      [ServiceMode.TAKEOUT]: enableTakeout,
    }),
    [enableDelivery, enableDriveThru, enableDineIn, enableMobileOrderDriveThru, enableTakeout]
  );
  const isMobileOrderDriveThruEnabled = useFlag(LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU);
  const restaurantsConfig = getConfigValue({ key: 'restaurants', defaultValue: {} });
  const validMobileOrderingEnvs = useMemo(() => restaurantsConfig.validMobileOrderingEnvs ?? [], [
    restaurantsConfig.validMobileOrderingEnvs,
  ]);

  const getStoreServiceModeStatuses = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      let statusByServiceMode = buildServiceModeStatusesForStore(
        store,
        storeLocalTime ?? store.localTime,
        enabledServiceModesFlagMap
      ).map(serviceModeStatus => {
        // Overrides MOBILE_ORDER_DRIVE_THRU availability based on mobileOrderDriveThruStores flag
        if (serviceModeStatus.serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU) {
          const isMobileOrderDriveThruEnabledForStore =
            !serviceModeStatus.featureFlagDisabled &&
            (mobileOrderDriveThruStores
              ? mobileOrderDriveThruStores.split(',').includes(store?.number ?? '')
              : false);
          return {
            ...serviceModeStatus,
            capable:
              isMobileOrderDriveThruEnabled &&
              isMobileOrderDriveThruEnabledForStore &&
              serviceModeStatus.capable,
            available:
              isMobileOrderDriveThruEnabled &&
              isMobileOrderDriveThruEnabledForStore &&
              serviceModeStatus.available,
            featureFlagDisabled:
              !isMobileOrderDriveThruEnabled || !isMobileOrderDriveThruEnabledForStore,
          };
        }
        return serviceModeStatus;
      });

      statusByServiceMode = statusByServiceMode.map(serviceModeStatus => {
        // Overrides DRIVE_THRU availability based on MOBILE_ORDER_DRIVE_THRU availability
        if (serviceModeStatus.serviceMode === ServiceMode.DRIVE_THRU) {
          const isMobileOrderDriveThruAvailable = statusByServiceMode.find(
            sms => sms.serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU
          )?.available;
          return {
            ...serviceModeStatus,
            available: !isMobileOrderDriveThruAvailable && serviceModeStatus.available,
          };
        }

        // Overrides TAKEOUT availability based on EAT_IN open hours
        if (serviceModeStatus.serviceMode === ServiceMode.TAKEOUT) {
          const isDiningOpen = !!statusByServiceMode.find(
            sms => sms.serviceMode === ServiceMode.EAT_IN
          )?.open;

          return {
            ...serviceModeStatus,
            available: serviceModeStatus.capable && isDiningOpen,
            open: isDiningOpen,
          };
        }
        return serviceModeStatus;
      });

      return statusByServiceMode;
    },
    [enabledServiceModesFlagMap, isMobileOrderDriveThruEnabled, mobileOrderDriveThruStores]
  );

  const isOpenForDelivery = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const serviceModeStatuses = getStoreServiceModeStatuses(store, storeLocalTime);
      const serviceModeStatus = serviceModeStatuses.find(
        serviceModeStatus => serviceModeStatus.serviceMode === ServiceMode.DELIVERY
      );

      return !!serviceModeStatus?.open;
    },
    [getStoreServiceModeStatuses]
  );

  const isDisabledForDelivery = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const serviceModeStatuses = getStoreServiceModeStatuses(store, storeLocalTime);
      const serviceModeStatus = serviceModeStatuses.find(
        serviceModeStatus => serviceModeStatus.serviceMode === ServiceMode.DELIVERY
      );

      return !!serviceModeStatus?.featureFlagDisabled;
    },
    [getStoreServiceModeStatuses]
  );

  const isOpenForPickup = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const statusByServiceMode = getStoreServiceModeStatuses(store, storeLocalTime);
      return statusByServiceMode
        .filter(serviceModeStatus => serviceModeStatus.serviceMode !== ServiceMode.DELIVERY)
        .some(serviceModeStatus => serviceModeStatus.open);
    },
    [getStoreServiceModeStatuses]
  );

  const isDisabledForPickup = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const statusByServiceMode = getStoreServiceModeStatuses(store, storeLocalTime);
      return statusByServiceMode
        .filter(serviceModeStatus => serviceModeStatus.serviceMode !== ServiceMode.DELIVERY)
        .every(serviceModeStatus => serviceModeStatus.featureFlagDisabled);
    },
    [getStoreServiceModeStatuses]
  );

  // Note that this is different from isAvailable
  const isOpenByServiceMode = useCallback(
    (store: IRestaurant, serviceMode: ServiceMode | null, storeLocalTime?: string | null) => {
      const statusByServiceMode = getStoreServiceModeStatuses(store, storeLocalTime);
      const serviceModeStatus = statusByServiceMode.find(
        serviceModeStatus => serviceModeStatus.serviceMode === serviceMode
      );
      return !!serviceModeStatus?.open;
    },

    [getStoreServiceModeStatuses]
  );

  // A restaurant is considered temporarily closed when all three hour sets
  // (delivery, dining room, drive-thru) have null open and close for today's day of the week.
  const isRestaurantTemporarilyClosed = (
    store: IRestaurant,
    storeLocalTime?: string | null
  ): boolean => {
    const { diningRoomHours, driveThruHours } = store;
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thr', 'fri', 'sat'] as const;
    const day = dayKeys[new Date(storeLocalTime ?? store.localTime ?? Date.now()).getDay()];
    const isClosedForDay = (hours: typeof diningRoomHours) =>
      !hours || (hours[`${day}Open`] === null && hours[`${day}Close`] === null);
    return isClosedForDay(diningRoomHours) && isClosedForDay(driveThruHours);
  };

  const isPOSAvailable = (store: IRestaurant) => isAvailable(store as IAvailabilityProps);
  const isMobileOrderingAvailable = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) =>
      checkIsMobileOrderingAvailable(store, validMobileOrderingEnvs) &&
      !isRestaurantTemporarilyClosed(store, storeLocalTime),
    [validMobileOrderingEnvs]
  );

  const isStoreAvailableForPickup = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      return (
        isAvailable(store as IAvailabilityProps | undefined) && // Store POS is available
        isMobileOrderingAvailable(store, storeLocalTime) &&
        isOpenForPickup(store, storeLocalTime) &&
        !isDisabledForPickup(store, storeLocalTime)
      );
    },
    [isDisabledForPickup, isMobileOrderingAvailable, isOpenForPickup]
  );

  const isStoreAvailableForDelivery = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      return (
        isAvailable(store as IAvailabilityProps | undefined) && // Store POS is available
        isMobileOrderingAvailable(store, storeLocalTime) &&
        isOpenForDelivery(store, storeLocalTime) &&
        !isDisabledForDelivery(store, storeLocalTime)
      );
    },
    [isDisabledForDelivery, isMobileOrderingAvailable, isOpenForDelivery]
  );

  const getPickupAvailableServiceModes = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const statusByServiceMode = getStoreServiceModeStatuses(store, storeLocalTime);
      return statusByServiceMode
        .filter(serviceModeStatus => serviceModeStatus.serviceMode !== ServiceMode.DELIVERY)
        .filter(
          serviceModeStatus => serviceModeStatus.available && !serviceModeStatus.featureFlagDisabled
        )
        .map(serviceModeStatus => serviceModeStatus.serviceMode);
    },
    [getStoreServiceModeStatuses]
  );

  const getPickupOpenServiceModes = useCallback(
    (store: IRestaurant, storeLocalTime?: string | null) => {
      const statusByServiceMode = getStoreServiceModeStatuses(store, storeLocalTime);
      return statusByServiceMode
        .filter(serviceModeStatus => serviceModeStatus.serviceMode !== ServiceMode.DELIVERY)
        .filter(serviceModeStatus => serviceModeStatus.open)
        .map(serviceModeStatus => serviceModeStatus.serviceMode);
    },
    [getStoreServiceModeStatuses]
  );

  const isServiceModeCapable = useCallback(
    (store: IRestaurant, serviceMode: ServiceMode | null, storeLocalTime?: string | null) => {
      const serviceModeStatuses = getStoreServiceModeStatuses(store, storeLocalTime);
      const status = serviceModeStatuses.find(sms => sms.serviceMode === serviceMode);
      return !!status?.capable && !status?.featureFlagDisabled;
    },
    [getStoreServiceModeStatuses]
  );

  const isServiceModeAvailable = useCallback(
    (store: IRestaurant, serviceMode: ServiceMode | null, storeLocalTime?: string | null) => {
      const serviceModeStatuses = getStoreServiceModeStatuses(store, storeLocalTime);
      const status = serviceModeStatuses.find(sms => sms.serviceMode === serviceMode);
      return !!status?.available && !status?.featureFlagDisabled;
    },
    [getStoreServiceModeStatuses]
  );

  const isServiceModeDisabled = useCallback(
    (store: IRestaurant, serviceMode: ServiceMode | null, storeLocalTime?: string | null) => {
      const serviceModeStatuses = getStoreServiceModeStatuses(store, storeLocalTime);
      const status = serviceModeStatuses.find(sms => sms.serviceMode === serviceMode);
      return !!status?.featureFlagDisabled;
    },
    [getStoreServiceModeStatuses]
  );

  const isStoreAvailable = useCallback(
    (
      store: IRestaurant,
      serviceMode: ServiceMode | null | undefined,
      storeLocalTime?: string | null
    ) => {
      if (!serviceMode) {
        // If no service mode is selected, check if the store is open for delivery
        return (
          isAvailable(store as IAvailabilityProps | undefined) &&
          isMobileOrderingAvailable(store, storeLocalTime) &&
          isOpenForDelivery(store, storeLocalTime)
        );
      }

      if (serviceMode === ServiceMode.DELIVERY) {
        // If no service mode is selected or delivery is selected, check if the store is open for delivery
        return isStoreAvailableForDelivery(store, storeLocalTime);
      }

      return (
        isStoreAvailableForPickup(store, storeLocalTime) &&
        isServiceModeAvailable(store, serviceMode, storeLocalTime)
      );
    },
    [
      isMobileOrderingAvailable,
      isOpenForDelivery,
      isServiceModeAvailable,
      isStoreAvailableForDelivery,
      isStoreAvailableForPickup,
    ]
  );

  return {
    getStoreServiceModeStatuses,
    isOpenForDelivery,
    isOpenForPickup,
    isOpenByServiceMode,
    isMobileOrderingAvailable,
    getPickupAvailableServiceModes,
    getPickupOpenServiceModes,
    isServiceModeCapable,
    isServiceModeAvailable,
    isStoreAvailable,
    isServiceModeDisabled,
    isStoreAvailableForPickup,
    isPOSAvailable,
    isStoreAvailableForDelivery,
  };
};
